import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiBriefcase, 
  FiUsers, 
  FiClipboard, 
  FiFileText, 
  FiTrendingUp, 
  FiTrendingDown,
  FiArrowRight,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiMoreHorizontal,
  FiLoader,
  FiMessageSquare
} from 'react-icons/fi';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { dashboardApi, documentsApi } from '../../services/api';
import { stripShareMeta, getActionBadgeColor } from '../../utils/activityDetails';
import { useAuth } from '../../context/AuthContext';
import { useAccessCheck } from '../../hooks/useAccessCheck';

const Dashboard = () => {
  const navigate = useNavigate();
  const { role, user } = useAuth();
  // Recent Activity is super admin only (not assessmentadmin / reviewer / trustcenteruser)
  const isSuperAdmin = role === 'admin';
  // Admins see every document; other roles only the ones shared with them
  const canViewAllDocuments = role === 'admin' || role === 'assessmentadmin';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useAccessCheck('cud_dashboard', '/requests');
  
  // API-driven data
  const [trustCenterStats, setTrustCenterStats] = useState(null);
  const [assessmentTrend, setAssessmentTrend] = useState([]);
  const [complianceData, setComplianceData] = useState([]);
  const [riskDistribution, setRiskDistribution] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [documentCount, setDocumentCount] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const activitiesPerPage = 8;

  // Documents the signed-in user is allowed to see: admins get every document,
  // everyone else only the ones shared with their user id / email.
  const fetchAccessibleDocumentCount = useCallback(async () => {
    try {
      if (canViewAllDocuments) {
        const res = await documentsApi.getAll(1, 10000);
        const items = Array.isArray(res) ? res : res?.data ?? [];
        return res?.total ?? items.length;
      }

      if (!user?.id && !user?.email) return 0;

      const res = await documentsApi.getForUser(user.id || null, user.email || null, 1, 10000);
      const items = Array.isArray(res) ? res : res?.data ?? [];
      // The API returns the full list, so keep only docs explicitly shared with this user
      return items.filter((doc) => {
        const byId = user?.id && doc.sharedWithUserId &&
          String(doc.sharedWithUserId) === String(user.id);
        const byEmail = user?.email && doc.sharedWithUserEmail &&
          doc.sharedWithUserEmail.toLowerCase() === user.email.toLowerCase();
        return byId || byEmail;
      }).length;
    } catch (err) {
      // A document failure shouldn't take down the whole dashboard
      console.error('Failed to fetch documents for user:', err);
      return 0;
    }
  }, [canViewAllDocuments, user?.id, user?.email]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch core data; only fetch activity if admin
      const promises = [
        dashboardApi.getTrustCenterStats(),
        dashboardApi.getAssessmentTrends(),
        dashboardApi.getComplianceData(),
        dashboardApi.getRiskDistribution(),
        fetchAccessibleDocumentCount(),
      ];
      
      // Only fetch activity data for super admin users. Pulled deeper than one page so the
      // panel's summary counts describe a meaningful window, not just the visible rows.
      if (isSuperAdmin) {
        promises.push(dashboardApi.getActivity(40));
      }

      const results = await Promise.all(promises);

      setTrustCenterStats(results[0]);
      setAssessmentTrend(results[1]);
      setComplianceData(results[2]);
      setRiskDistribution(results[3]);
      setDocumentCount(results[4]);

      if (isSuperAdmin && results[5]) {
        setRecentActivities(Array.isArray(results[5]) ? results[5] : []);
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, fetchAccessibleDocumentCount]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // console.log(' wow Trust Center Stats:', trustCenterStats);

  // Stats cards - dynamically built from API data
  const stats = trustCenterStats ? [
    { 
      title: 'Organizations', 
      value: trustCenterStats.totalOrganizations?.toLocaleString() || '0', 
      change: '+15%', 
      trend: 'up', 
      icon: FiClipboard,
      color: 'from-[#0077b5] to-[#0077b5]',
      bgColor: 'bg-[#0077b5]/10',
      textColor: 'text-blue-600',
      referTo:'/organizations'
    },
    { 
      title: 'Pending Assessments', 
      value: trustCenterStats.pendingAssessments?.toString() || '0', 
      change: '-5%', 
      trend: 'down', 
      icon: FiAlertCircle,
      color: 'from-[#F47F2E] to-[#F47F2E]',
      bgColor: 'bg-[#F47F2E]/10',
      textColor: 'text-amber-600',
      referTo:'/assessments'
    },
    { 
      title: 'Compliance Score', 
      value: `${trustCenterStats.complianceScore || 0}%`, 
      change: '+2.4%', 
      trend: 'up', 
      icon: FiCheckCircle,
      color: 'from-[#0077b5] to-[#0077b5]',
      bgColor: 'bg-[#0077b5]/10',
      textColor: 'text-violet-600',
      referTo:null
    },
    {
      title: canViewAllDocuments ? 'Documents' : 'My Documents',
      value: (documentCount ?? 0).toLocaleString(),
      change: '+8%',
      trend: 'up',
      icon: FiFileText,
      color: 'from-[#F47F2E] to-[#F47F2E]',
      bgColor: 'bg-[#0077b5]/10',
      textColor: 'text-emerald-600',
      referTo:'/documents'
    },
  ] : [];

  const getActivityStatus = (activity) => {
    const actionText = (activity?.action || '').toLowerCase();
    const entityTypeText = (activity?.entityType || '').toLowerCase();

    if (actionText.includes('alert') || actionText.includes('failed') || actionText.includes('error')) {
      return 'error';
    }

    if (actionText.includes('pending') || actionText.includes('review')) {
      return 'warning';
    }

    if (actionText.includes('completed') || actionText.includes('added') || actionText.includes('onboarded')) {
      return 'success';
    }

    if (entityTypeText.includes('risk')) {
      return 'warning';
    }

    return 'info';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <FiCheckCircle className="text-[#0077b5]" />;
      case 'warning': return <FiClock className="text-[#F47F2E]" />;
      case 'error': return <FiAlertCircle className="text-[#F47F2E]" />;
      default: return <FiClipboard className="text-[#0077b5]" />;
    }
  };

  // Entity icons mirror the Activity page so the same entity reads the same in both places.
  const getEntityIcon = (type) => {
    switch ((type || '').toLowerCase()) {
      case 'user': return <FiUsers className="text-blue-500" />;
      case 'organization': return <FiBriefcase className="text-purple-500" />;
      case 'assessment': return <FiClipboard className="text-emerald-500" />;
      case 'questionnaire': return <FiFileText className="text-orange-500" />;
      default: return <FiFileText className="text-slate-400" />;
    }
  };

  const formatActivityTime = (timestamp) => {
    if (!timestamp) return null;
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // Headline counts over everything loaded, so the panel says something about the window as a
  // whole rather than only the page on screen.
  const activityCounts = recentActivities.reduce((acc, a) => {
    const action = (a.action || '').toLowerCase();
    if (action.includes('shared')) acc.shared += 1;
    else if (action.includes('created')) acc.created += 1;
    else if (action.includes('updated')) acc.updated += 1;
    else if (action.includes('deleted')) acc.deleted += 1;
    return acc;
  }, { shared: 0, created: 0, updated: 0, deleted: 0 });

  const activitySummary = [
    { label: 'Shared', value: activityCounts.shared, className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    { label: 'Created', value: activityCounts.created, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { label: 'Updated', value: activityCounts.updated, className: 'bg-blue-50 text-blue-700 border-blue-200' },
    { label: 'Deleted', value: activityCounts.deleted, className: 'bg-red-50 text-red-700 border-red-200' },
  ].filter(s => s.value > 0);



  if (loading) {
    const SkeletonItem = ({ className }) => (
      <div className={`bg-[#e6eef5] rounded-md ${className} animate-pulse`} />
    );

    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <SkeletonItem className="w-48 h-6 mb-2" />
            <SkeletonItem className="w-72 h-4" />
          </div>
        </div>

        {/* Stats Cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[0,1,2,3].map((i) => (
            <div key={i} className=" p-6 shadow-sm border bg-white cardStyle">
              <div className="flex items-start justify-between">
                <SkeletonItem className="w-12 h-12 rounded-xl" />
                <SkeletonItem className="w-16 h-4 rounded" />
              </div>
              <div className="mt-4">
                <SkeletonItem className="w-24 h-8 rounded" />
                <div className="mt-2"><SkeletonItem className="w-32 h-3 rounded" /></div>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Main Charts & Workflow skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Assessment Trends (detailed) */}
          <div className="lg:col-span-8 p-6 shadow-sm border bg-white cardStyle">
            <div className="flex items-center justify-between mb-4">
              <div className="space-y-2">
                <SkeletonItem className="w-44 h-6" />
                <SkeletonItem className="w-72 h-3" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonItem className="w-12 h-4" />
                <SkeletonItem className="w-12 h-4" />
              </div>
            </div>
            <div className="mb-3 flex items-center gap-3">
              {/* legend placeholders */}
              <div className="flex items-center gap-2">
                <SkeletonItem className="w-3 h-3 rounded-full" />
                <SkeletonItem className="w-24 h-3" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonItem className="w-3 h-3 rounded-full" />
                <SkeletonItem className="w-24 h-3" />
              </div>
            </div>
            <div className="w-full h-64 rounded">
              <SkeletonItem className="w-full h-56 rounded" />
              <div className="mt-3 flex items-center justify-between">
                <SkeletonItem className="w-24 h-3" />
                <div className="flex gap-2">
                  <SkeletonItem className="w-12 h-3" />
                  <SkeletonItem className="w-12 h-3" />
                  <SkeletonItem className="w-12 h-3" />
                </div>
              </div>
            </div>
          </div>

          {/* Assessment Workflow (detailed) */}
          <div className="lg:col-span-4  p-6 shadow-sm border bg-white cardStyle">
            <div className="mb-4">
              <SkeletonItem className="w-40 h-6" />
              <SkeletonItem className="w-56 h-3 mt-2" />
            </div>
            <div className="space-y-4">
              {[1,2,3,4].map((s) => (
                <div key={s} className="flex items-center gap-4">
                  <SkeletonItem className="w-12 h-12 rounded-xl flex-shrink-0" />
                  <div className="flex-1">
                    <SkeletonItem className="w-40 h-4 mb-2" />
                    <SkeletonItem className="w-56 h-3" />
                    <div className="mt-2 flex gap-2">
                      <SkeletonItem className="w-20 h-3" />
                      <SkeletonItem className="w-16 h-3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Risk Distribution & Compliance (detailed) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Risk Distribution detailed skeleton */}
          <div className=" p-6 shadow-sm border bg-white cardStyle">
            <div className="flex items-center justify-between mb-4">
              <div>
                <SkeletonItem className="w-40 h-6" />
                <SkeletonItem className="w-56 h-3 mt-2" />
              </div>
            </div>
            <div className="space-y-3">
              {[0,1,2,3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <SkeletonItem className="w-24 h-4" />
                  <SkeletonItem className="flex-1 h-4 rounded" />
                  <SkeletonItem className="w-12 h-4" />
                </div>
              ))}
            </div>
          </div>

          {/* Compliance Status detailed skeleton */}
          <div className=" p-6 shadow-sm border bg-white cardStyle">
            <div className="flex items-center justify-between mb-4">
              <div>
                <SkeletonItem className="w-40 h-6" />
                <SkeletonItem className="w-56 h-3 mt-2" />
              </div>
              <SkeletonItem className="w-12 h-4" />
            </div>
            <div className="flex items-center gap-6">
              <div className="w-1/2">
                <SkeletonItem className="w-full h-40 rounded" />
              </div>
              <div className="w-1/2">
                <div className="space-y-3">
                  {[0,1,2].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <SkeletonItem className="w-4 h-4 rounded-full" />
                      <SkeletonItem className="w-40 h-4" />
                      <SkeletonItem className="w-12 h-4" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity skeleton (super admin only) */}
        {isSuperAdmin && (
          <div className=" p-6 shadow-sm border bg-white cardStyle">
            <div className="flex items-center justify-between mb-6">
              <SkeletonItem className="w-40 h-6" />
              <SkeletonItem className="w-20 h-6" />
            </div>
            <div className="space-y-4">
              {[0,1,2,3,4].map((i) => (
                <div key={i} className="flex items-start gap-4 p-3 rounded-lg">
                  <SkeletonItem className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <SkeletonItem className="w-56 h-4 mb-2" />
                    <SkeletonItem className="w-40 h-3" />
                  </div>
                  <SkeletonItem className="w-24 h-4" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-xl border p-4 bg-[#ffffff] border-[#F47F2E] text-[#F47F2E]`}>
        Error loading dashboard: {error}
        <button onClick={fetchDashboardData} className="ml-4 underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="pageLabel">See how assessments are going and what users are doing.</p>
        </div>
        
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`p-6 bg-white cardStyle${stat.referTo ? ' cursor-pointer' : ''}`}
            // onClick={() => stat.referTo && (window.location.href = stat.referTo)}
            onClick={() => stat.referTo && (navigate(stat.referTo))}
          >
            <div className="flex items-start justify-between">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="text-white text-xl" />
              </div>
              {/* <div className={`flex items-center gap-1 text-sm font-medium ${stat.trend === 'up' ? 'text-[#0077b5]' : 'text-[#F47F2E]'}`}>
                {stat.trend === 'up' ? <FiTrendingUp /> : <FiTrendingDown />}
                {stat.change}
              </div> */}
            </div>
            <div className="mt-4">
              <h3 className={`text-2xl font-bold text-[#53545a]`}>{stat.value}</h3>
              <p className={`text-sm mt-1 text-[#919191]`}>{stat.title}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Assessment Trends & Workflow Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Assessment Trends Chart */}
        <div className={`lg:col-span-8 p-6 shadow-sm border bg-white `} style={{ boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)', border: '1px solid  #938f8f7c'}}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold text-[#53545a]`}>Assessment Trends</h2>
              <p className={`text-sm text-[#919191]`}>Completed vs Pending assessments</p>
            </div>
            <button className={`p-2 rounded-lg transition-colors hover:bg-[#919191]/10`}>
              <FiMoreHorizontal className="text-[#919191]" />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={assessmentTrend}>
              <defs>
                <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0077b5" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#0077b5" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F47F2E" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#F47F2E" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#919191" />
              <XAxis dataKey="month" stroke="#919191" fontSize={12} />
              <YAxis stroke="#919191" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  border: `1px solid #919191`, 
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  color: '#53545a'
                }} 
              />
              <Area type="monotone" dataKey="completed" stroke="#0077b5" strokeWidth={2} fillOpacity={1} fill="url(#colorCompleted)" />
              <Area type="monotone" dataKey="pending" stroke="#F47F2E" strokeWidth={2} fillOpacity={1} fill="url(#colorPending)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Assessment Workflow */}
        <div className={`lg:col-span-4 p-6 shadow-sm border bg-white cardStyle`}>
          <div className="mb-6">
            <h2 className={`text-lg font-semibold text-[#53545a]`}>Assessment Workflow</h2>
            <p className={`text-sm text-[#919191]`}>Standard process flow</p>
          </div>
          <div className="space-y-4">
            {[
              { step: 1, title: 'Account Setup', desc: 'Creates and shares assessments', icon: FiBriefcase, color: 'from-[#0077b5] to-[#0077b5]' },
              { step: 2, title: 'Discover Answers', desc: 'System/AI processes data', icon: FiClipboard, color: 'from-[#F47F2E] to-[#F47F2E]' },
              { step: 3, title: 'Review Process', desc: 'SME/Reviewer approves/rejects', icon: FiCheckCircle, color: 'from-[#0077b5] to-[#0077b5]' },
              { step: 4, title: 'Feedback Process', desc: 'Reviewer notes sent back for revision', icon: FiMessageSquare, color: 'from-[#F47F2E] to-[#F47F2E]' },
              { step: 5, title: 'View Results', desc: 'User accesses final report', icon: FiUsers, color: 'from-[#0077b5] to-[#0077b5]' },
            ].map((item) => (
              <div key={item.step} className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0`}>
                  <item.icon className="text-white text-lg" />
                </div>
                <div className="flex-1">
                  <p className={`font-medium text-[#53545a]`}>{item.title}</p>
                  <p className={`text-sm text-[#919191]`}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Distribution */}
        <div className={` p-6 shadow-sm border bg-white cardStyle`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold text-[#53545a]`}>Risk Distribution</h2>
              <p className={`text-sm text-[#919191]`}>By severity level</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={riskDistribution} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#919191" horizontal={false} />
              <XAxis type="number" stroke="#919191" fontSize={12} />
              <YAxis type="category" dataKey="category" stroke="#919191" fontSize={12} width={60} />
              <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: `1px solid #919191`, color: '#53545a' }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {riskDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Compliance Status Pie Chart */}
        <div className={` p-6 shadow-sm border bg-white cardStyle`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold text-[#53545a]`}>Compliance Status</h2>
              <p className={`text-sm text-[#919191]`}>Overall distribution</p>
            </div>
            <button className="p-2 hover:bg-[#919191]/10 rounded-lg transition-colors">
              <FiMoreHorizontal className="text-[#919191]" />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={complianceData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {complianceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-4">
            {complianceData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className={`text-sm text-[#919191]`}>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity - Super Admin Only */}
      {isSuperAdmin && (
      <div className={` p-6 shadow-sm border bg-white cardStyle`}>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h2 className={`text-lg font-semibold text-[#53545a]`}>Recent Activity</h2>
            <p className={`text-sm text-[#919191]`}>Latest updates across the platform</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {activitySummary.map(s => (
              <span
                key={s.label}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.className}`}
              >
                <span className="font-semibold">{s.value}</span> {s.label}
              </span>
            ))}
            <button
              onClick={() => navigate('/activity')}
              className="text-sm text-[#F47F2E] hover:text-[#F47F2E] font-medium flex items-center gap-1"
            >
              View All <FiArrowRight />
            </button>
          </div>
        </div>

        <div className="border border-[#919191]/20 rounded-lg overflow-hidden">
          {/* Column headers — hidden on narrow screens, where each row stacks instead */}
          {recentActivities.length > 0 && (
            <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-2 bg-[#919191]/5 border-b border-[#919191]/20">
              <p className="col-span-3 text-[11px] font-semibold text-[#919191] uppercase tracking-wide">Action</p>
              <p className="col-span-3 text-[11px] font-semibold text-[#919191] uppercase tracking-wide">Entity</p>
              <p className="col-span-3 text-[11px] font-semibold text-[#919191] uppercase tracking-wide">Details</p>
              <p className="col-span-3 text-[11px] font-semibold text-[#919191] uppercase tracking-wide">User</p>
            </div>
          )}

          <div className="divide-y divide-[#919191]/15">
            {recentActivities.slice(activityPage * activitiesPerPage, (activityPage + 1) * activitiesPerPage).map((activity, index) => {
              // Share rows carry a JSON payload after the prose; never render it here.
              const details = stripShareMeta(activity.details);
              const stamp = formatActivityTime(activity.timestamp);
              return (
                <div
                  key={activity.id || `${activity.timestamp || index}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-3 px-4 py-3 transition-colors hover:bg-[#919191]/5 cursor-pointer"
                  onClick={() => navigate('/activity')}
                >
                  {/* Action */}
                  <div className="lg:col-span-3 flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#919191]/10">
                      {getStatusIcon(getActivityStatus(activity))}
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getActionBadgeColor(activity.action)}`}>
                      {activity.action || '-'}
                    </span>
                  </div>

                  {/* Entity */}
                  <div className="lg:col-span-3 flex items-center gap-2 min-w-0 pl-10 lg:pl-0">
                    <span className="flex-shrink-0">{getEntityIcon(activity.entityType)}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#53545a] truncate">{activity.entityName || '-'}</p>
                      <p className="text-xs text-[#919191]">{activity.entityType || '-'}</p>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="lg:col-span-3 min-w-0 pl-10 lg:pl-0">
                    <p className="text-sm text-[#919191] line-clamp-2 break-words" title={details}>
                      {details || '—'}
                    </p>
                  </div>

                  {/* User + when */}
                  <div className="lg:col-span-3 flex items-center gap-2 min-w-0 pl-10 lg:pl-0">
                    <div className="w-7 h-7 rounded-full bg-[#0077b5] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {(activity.userEmail || 'U')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-[#53545a] truncate" title={activity.userEmail}>{activity.userEmail || '-'}</p>
                      <p className="text-xs">
                        <span className="text-[#F47F2E]">{activity.userRole || '-'}</span>
                        <span className="text-[#919191]"> · {activity.timeAgo || stamp || '-'}</span>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            {recentActivities.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#919191]">No recent activity found.</div>
            )}
          </div>
        </div>
        {recentActivities.length > activitiesPerPage && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#919191]/20">
            <span className="text-sm text-[#919191]">
              Showing {activityPage * activitiesPerPage + 1}–{Math.min((activityPage + 1) * activitiesPerPage, recentActivities.length)} of {recentActivities.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActivityPage(p => p - 1)}
                disabled={activityPage === 0}
                className="p-1.5 rounded-lg transition-colors hover:bg-[#919191]/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiChevronLeft className="text-[#53545a]" />
              </button>
              <span className="text-sm font-medium text-[#53545a]">
                {activityPage + 1} / {Math.ceil(recentActivities.length / activitiesPerPage)}
              </span>
              <button
                onClick={() => setActivityPage(p => p + 1)}
                disabled={(activityPage + 1) * activitiesPerPage >= recentActivities.length}
                className="p-1.5 rounded-lg transition-colors hover:bg-[#919191]/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiChevronRight className="text-[#53545a]" />
              </button>
            </div>
          </div>
        )}
      </div>
      )}

    </div>
  );
};

export default Dashboard;